/**
 * Аватар игрока.
 *
 * Пока картинки нет — первая буква имени на цветном круге. Цвет выводится из
 * имени, поэтому у одного и того же человека он всегда одинаковый: так список
 * соигроков читается взглядом, а не побуквенно.
 *
 * Персонал обведён рамкой: администратор — янтарной, модератор — серой.
 * Метка нужна не владельцу аккаунта, а окружающим — в комнате и в лидерборде
 * должно быть видно, кто отвечает за содержимое, поэтому роль едет в публичном
 * профиле и рамка рисуется везде, где показывается лицо.
 */
import { useTranslation } from "react-i18next";

import type { Role } from "../api/types";

const COLORS = [
  "bg-amber-500/25 text-amber-200",
  "bg-sage/25 text-sage",
  "bg-rust/25 text-rust",
  "bg-ink-600/40 text-ink-200",
  "bg-amber-700/30 text-amber-300",
];

function colorFor(name: string): string {
  let sum = 0;
  for (const char of name) sum += char.codePointAt(0) ?? 0;
  return COLORS[sum % COLORS.length];
}

/*
 * Рамка нарисована ring, а не border: border съел бы внутренний размер, и
 * ряд аватарок разъехался бы по высоте. Отступа под рамкой нет намеренно —
 * подложка у аватарки везде разная, и ring-offset пришлось бы задавать
 * на каждом месте отдельно.
 */
const RING: Partial<Record<Role, string>> = {
  ROLE_ADMIN: "ring-2 ring-amber-400",
  ROLE_MODERATOR: "ring-2 ring-ink-400",
};

const ROLE_LABEL: Partial<Record<Role, string>> = {
  ROLE_ADMIN: "role.admin",
  ROLE_MODERATOR: "role.moderator",
};

export function Avatar({
  url,
  username,
  role,
  className = "h-10 w-10",
}: {
  url?: string;
  username: string;
  role?: Role;
  className?: string;
}) {
  const { t } = useTranslation();

  const ring = role ? RING[role] : undefined;
  // Цветной кружок без объяснения — загадка, поэтому у рамки есть подпись.
  const label = role ? ROLE_LABEL[role] : undefined;
  const title = label ? t(label) : undefined;

  if (url) {
    return (
      <img
        src={url}
        alt={username}
        title={title}
        className={`shrink-0 rounded-full border border-ink-700 object-cover ${ring ?? ""} ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden
      title={title}
      className={`flex shrink-0 items-center justify-center rounded-full border border-ink-700
                  font-semibold uppercase ${colorFor(username)} ${ring ?? ""} ${className}`}
    >
      {username.slice(0, 1)}
    </span>
  );
}
